'use client'

import type { Coordinates, Location } from 'maps/types'
import { ENDPOINT_GEOCODING_SERVICE } from 'maps/utils/constants'
import { fetcher } from 'services/rest/fetcher'
import { env } from 'utils/envManager/envManager'

enum AddressComponentType {
	PostalCodes = 'postal_codes',
	Locality = 'locality',
	Route = 'route',
}

interface GeocodeProps {
	address?: string
	coordinates?: Coordinates
	languageISO: string
	countryISO: string
}

const getComponentValue = (
	component: woosmap.map.localities.AddressComponents | undefined
): string | undefined => {
	if (!component) {
		return undefined
	}
	const value = component.long_name
	return Array.isArray(value) ? value[0] : value
}

const roundCoordinate = (coordinate: number, decimals = 3): number =>
	Number.parseFloat(coordinate.toFixed(decimals))

const getParams = (props: GeocodeProps) => {
	const { address, coordinates, languageISO, countryISO } = props
	const params: Record<string, string> = {
		language: languageISO,
		components: `country:${countryISO}`,
		types: 'city|locality|postal_code|address',
		key: env.NEXT_PUBLIC_WOOSMAP_API_KEY,
		// Avoid sending cookies for a third-party service request
		credentials: 'omit',
		// Enable long term caching in CDN
		longCache: 'true',
	}

	if (address) {
		params.address = `${address}, ${countryISO}`.toLowerCase()
	}

	if (!(address || coordinates)) {
		params.address = countryISO.toLowerCase()
	}

	if (coordinates) {
		const lat = roundCoordinate(coordinates.lat)
		const lng = roundCoordinate(coordinates.lng)
		const latlng = `${lat},${lng}`
		params.latlng = latlng
	}

	return params
}

/** Get the coordinates from an address (geocode) or an address from coordinates (reverse) */
export async function geocode(props: GeocodeProps): Promise<Location | null> {
	const { address } = props
	try {
		const url = new URL(ENDPOINT_GEOCODING_SERVICE, env.NEXT_PUBLIC_API_DOMAIN)
		const params = getParams(props)

		url.search = new URLSearchParams(params).toString()

		const data =
			await fetcher<woosmap.map.localities.LocalitiesGeocodeResponse>(url.href)

		if (!data?.results || data.results.length === 0) {
			return null
		}

		const { geometry, address_components } = data.results[0]
		const { lat, lng } = geometry?.location ?? {}

		if (!(lat && lng)) {
			return null
		}

		const postalCode = getComponentValue(
			address_components?.find((component) =>
				component.types.includes(AddressComponentType.PostalCodes)
			)
		)
		const cityName = getComponentValue(
			address_components?.find((component) =>
				component.types.includes(AddressComponentType.Locality)
			)
		)
		const route = getComponentValue(
			address_components?.find((component) =>
				component.types.includes(AddressComponentType.Route)
			)
		)

		// In the case of a reverse request, the address must be created from the response
		const fullAddress =
			address ??
			`${route ?? ''} ${postalCode ?? ''} ${cityName ?? ''}`
				.replace(/\s+/g, ' ')
				.trim()

		return {
			coordinates: { lat, lng },
			addressInfo: { address: fullAddress, postalCode, cityName },
		}
	} catch {
		console.error(`[Maps] Error geocoding address [${address}]`)
		return null
	}
}
